import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

export const otpApiError = (err, t) => {
  const d = err?.response?.data?.detail;
  const map = {
    OTP_SEND_FAILED: t.otp.sendFailed,
    OTP_INVALID: t.otp.invalid,
    PHONE_TAKEN: t.otp.phoneTaken,
    INVALID_PHONE: t.otp.invalidPhone,
    PHONE_NOT_VERIFIED: t.otp.notVerified,
  };
  return map[d] || (typeof d === "string" ? d : t.otp.sendFailed);
};

const OtpModal = ({ open, phone, onClose, onVerified }) => {
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const sentRef = useRef(false);

  const sendCode = useCallback(async () => {
    setSending(true); setError("");
    try {
      await api.post("/auth/send-otp", { phone });
      setCooldown(30);
    } catch (err) { setError(otpApiError(err, t)); }
    finally { setSending(false); }
  }, [phone, t]);

  useEffect(() => {
    if (open && !sentRef.current) { sentRef.current = true; sendCode(); }
    if (!open) { sentRef.current = false; setCode(""); setError(""); setCooldown(0); }
  }, [open, sendCode]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const verify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setVerifying(true); setError("");
    try {
      await api.post("/auth/verify-otp", { phone, code: code.trim() });
      onVerified();
    } catch (err) { setError(otpApiError(err, t)); }
    finally { setVerifying(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="otp-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream hover:bg-gray-200 flex items-center justify-center" data-testid="otp-close">
          <X size={16} />
        </button>
        <span className="w-12 h-12 rounded-2xl bg-teal/10 text-teal flex items-center justify-center">
          <ShieldCheck size={22} />
        </span>
        <h3 className="font-display text-2xl text-teal-deep mt-4 leading-tight">{t.otp.title}</h3>
        <p className="text-sm text-teal-soft mt-2">
          {sending ? t.otp.sending : <>{t.otp.sub} <strong className="text-teal-deep">{phone}</strong>.</>}
        </p>
        <form onSubmit={verify} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.otp.codeLabel}</label>
            <input
              autoFocus inputMode="numeric" pattern="[0-9]*" maxLength={6}
              className="field-input mt-1 text-center text-2xl tracking-[0.5em] font-bold"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="otp-code-input"
            />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="otp-error">{error}</p>}
          <button type="submit" disabled={verifying || code.length < 4} className="btn-orange w-full justify-center" data-testid="otp-verify-btn">
            {verifying ? "…" : t.otp.verify}
          </button>
          <button
            type="button" onClick={sendCode} disabled={cooldown > 0 || sending}
            className="w-full text-xs font-bold text-teal hover:text-orange disabled:text-gray-400 transition-colors"
            data-testid="otp-resend-btn"
          >
            {cooldown > 0 ? t.otp.resendIn.replace("{s}", cooldown) : t.otp.resend}
          </button>
        </form>
      </div>
    </div>
  );
};

export default OtpModal;
